import { useState, type ReactNode } from "react";
import { useNotify, useRecordContext, useRefresh } from "ra-core";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { cn } from "@/lib/utils";

import { desconectarWhatsapp, DialogoWhatsapp, enviarTesteWhatsapp, resumoWhatsapp, useConexaoWhatsapp, type LinhaWhatsapp } from "./WhatsappConexao";
import {
  desconectarAzosPortal,
  DialogoAzosPortal,
  resumoAzosPortal,
  useConexaoAzosPortal,
  type LinhaAzosPortal,
} from "./AzosPortalConexao";
import { resumoGoogle, useAcoesGoogle, useConexaoGoogle, type LinhaGoogle } from "./GoogleConexao";

/**
 * Conexões (28/10/2026, Rick): as contas externas que o sistema usa em nome do
 * usuário, num lugar só — cartões no Meu perfil e aba no Editar do usuário.
 *
 * Servopa e Canopus gravam pelas RPCs set_user_*_credentials; desde a 275 o
 * próprio usuário grava o login dele; Rodobens (Escritório Digital) do mesmo
 * jeito desde a 301, e o robô da Rodobens entra com ela. MAG é pela corretora da North. Azos:
 * cotação e apólices pela North; com o login do portal do corretor, também
 * comissões, recebimentos e campanhas (AzosPortalConexao.tsx, 288). WhatsApp: o briefing no número do próprio
 * usuário (WhatsappConexao.tsx, migration 285). Google: Gmail para enviar e
 * Agenda, pela conta do próprio usuário (GoogleConexao.tsx, migration 302).
 */

export type EstadoConexao = "conectado" | "incompleto" | "nao_conectado" | "em_breve";

const SELO: Record<EstadoConexao, { texto: string; classe: string }> = {
  conectado: { texto: "Conectado", classe: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300" },
  incompleto: { texto: "Incompleto", classe: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300" },
  nao_conectado: { texto: "Não conectado", classe: "bg-muted text-muted-foreground" },
  em_breve: { texto: "Em breve", classe: "bg-muted text-muted-foreground" },
};

export const SeloConexao = ({ estado }: { estado: EstadoConexao }) => (
  <span className={cn("whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold", (SELO[estado] ?? SELO.nao_conectado).classe)}>
    {(SELO[estado] ?? SELO.nao_conectado).texto}
  </span>
);

/** Login + senha no Vault: os dois = conectado; só um = incompleto. */
export const estadoCredencial = (login: string | null | undefined, senhaDefinida: boolean | null | undefined): EstadoConexao =>
  login && senhaDefinida ? "conectado" : login || senhaDefinida ? "incompleto" : "nao_conectado";

export type RegistroConexoes = {
  id?: number | string;
  servopa_username?: string | null;
  servopa_senha_definida?: boolean | null;
  canopus_username?: string | null;
  canopus_senha_definida?: boolean | null;
  rodobens_username?: string | null;
  rodobens_senha_definida?: boolean | null;
};

type Conexao = {
  codigo: "whatsapp" | "servopa" | "canopus" | "rodobens" | "azos" | "mag" | "gmail";
  sigla: string;
  nome: string;
  descricao: string;
  estado: EstadoConexao;
  detalhe: string;
};

export const conexoesDoUsuario = (
  r: RegistroConexoes | undefined,
  superAdmin: boolean,
  whatsapp: LinhaWhatsapp | null = null,
  azos: LinhaAzosPortal | null = null,
  google: LinhaGoogle | null = null,
): Conexao[] => {
  const wa = resumoWhatsapp(whatsapp);
  const az = resumoAzosPortal(azos);
  const gg = resumoGoogle(google);
  const servopa = estadoCredencial(r?.servopa_username, r?.servopa_senha_definida);
  const canopus = estadoCredencial(r?.canopus_username, r?.canopus_senha_definida);
  const rodobens = estadoCredencial(r?.rodobens_username, r?.rodobens_senha_definida);
  const lista: Conexao[] = [
    {
      codigo: "whatsapp",
      sigla: "WA",
      nome: "WhatsApp",
      descricao: "Mensagens da North IA pelo número do usuário.",
      estado: wa.estado,
      detalhe: wa.detalhe,
    },
    {
      codigo: "servopa",
      sigla: "SV",
      nome: "Servopa",
      descricao: "Lances, inadimplentes e comissões.",
      estado: servopa,
      detalhe:
        servopa === "conectado"
          ? `Login ${r?.servopa_username} · senha cadastrada`
          : servopa === "incompleto"
            ? "Cadastro incompleto: login e senha necessários para vender."
            : "Login e senha necessários para vender.",
    },
    {
      codigo: "canopus",
      sigla: "CN",
      nome: "Canopus",
      descricao: "Vendas pelo AFV Canopus.",
      estado: canopus,
      detalhe:
        canopus === "conectado"
          ? `Login ${r?.canopus_username} · senha cadastrada`
          : "Sem login próprio: vendas pela conta da North.",
    },
    {
      codigo: "rodobens",
      sigla: "RB",
      nome: "Rodobens",
      descricao: "Escritório Digital e Plataforma de Vendas.",
      estado: rodobens,
      detalhe:
        rodobens === "conectado"
          ? `Login ${r?.rodobens_username} · senha cadastrada`
          : rodobens === "incompleto"
            ? "Cadastro incompleto: login e senha necessários."
            : "Login e senha do Escritório Digital.",
    },
    {
      codigo: "azos",
      sigla: "AZ",
      nome: "Azos",
      descricao: "Comissões, recebimentos e campanhas do portal do corretor.",
      estado: az.estado,
      detalhe: az.detalhe,
    },
    {
      codigo: "mag",
      sigla: "MG",
      nome: "MAG",
      descricao: "Cotação e propostas de seguro de vida.",
      estado: "conectado",
      detalhe: "Pela corretora da North.",
    },
    {
      codigo: "gmail",
      sigla: "GM",
      nome: "Google",
      descricao: "Envio de e-mail pelo Gmail e Agenda Google.",
      estado: gg.estado,
      detalhe: gg.detalhe,
    },
  ];
  // Canopus e Rodobens ainda são só do super admin (módulos em teste).
  return superAdmin ? lista : lista.filter((c) => c.codigo !== "canopus" && c.codigo !== "rodobens");
};

export type CodigoConexao = Conexao["codigo"];

/** Ícone da empresa (web/public/logos/conexoes); sem arquivo ou com erro, cai na sigla. */
const ICONE: Record<CodigoConexao, string> = {
  whatsapp: "/logos/conexoes/whatsapp.svg",
  servopa: "/logos/conexoes/servopa.png",
  canopus: "/logos/conexoes/canopus.png",
  rodobens: "/logos/conexoes/rodobens.png",
  azos: "/logos/conexoes/azos.png",
  mag: "/logos/conexoes/mag.png",
  gmail: "/logos/conexoes/gmail.svg",
};

const IconeConexao = ({ codigo, sigla }: { codigo?: CodigoConexao; sigla: string }) => {
  const [falhou, setFalhou] = useState(false);
  if (!codigo || falhou) {
    return (
      <span
        aria-hidden="true"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-[11px] font-bold text-marinho dark:text-foreground"
      >
        {sigla}
      </span>
    );
  }
  return (
    <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-white">
      <img
        src={ICONE[codigo]}
        alt=""
        className={cn(
          "object-contain",
          codigo === "azos" ? "h-full w-full" : codigo === "whatsapp" || codigo === "mag" ? "h-7 w-7" : "h-6 w-6",
        )}
        onError={() => setFalhou(true)}
      />
    </span>
  );
};

/** Linha da aba Conexões do Editar usuário. `children` = campos abertos embaixo. */
export const LinhaConexao = ({
  codigo,
  sigla,
  nome,
  detalhe,
  estado,
  acao,
  children,
}: {
  codigo?: CodigoConexao;
  sigla: string;
  nome: string;
  detalhe: string;
  estado: EstadoConexao;
  acao?: ReactNode;
  children?: ReactNode;
}) => (
  <div className="flex flex-col gap-2.5 border-b px-4 py-3.5 last:border-b-0">
    <div className="flex items-center gap-3">
      <IconeConexao codigo={codigo} sigla={sigla} />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-sm font-semibold">{nome}</span>
        <span className="truncate text-xs text-muted-foreground">{detalhe}</span>
      </div>
      <SeloConexao estado={estado} />
      {acao}
    </div>
    {children && <div className="sm:pl-11">{children}</div>}
  </div>
);

/** As conexões que não se editam no Editar usuário (WhatsApp, Azos, MAG, Gmail). */
export const LinhasConexoesFixas = ({ crmUserId }: { crmUserId?: number | string | null }) => {
  const { linha } = useConexaoWhatsapp(crmUserId);
  const azos = useConexaoAzosPortal(crmUserId);
  const google = useConexaoGoogle(crmUserId);
  return (
    <>
      {conexoesDoUsuario(undefined, false, linha, azos.linha, google.linha)
        .filter((c) => c.codigo !== "servopa")
        .map((c) => (
          <LinhaConexao key={c.codigo} codigo={c.codigo} sigla={c.sigla} nome={c.nome} detalhe={c.detalhe} estado={c.estado} />
        ))}
    </>
  );
};

type Editavel = "servopa" | "canopus" | "rodobens";

const RPC: Record<Editavel, { rpc: string; login: string; senha: string; nome: string }> = {
  servopa: { rpc: "set_user_servopa_credentials", login: "p_servopa_username", senha: "p_servopa_password", nome: "Servopa" },
  canopus: { rpc: "set_user_canopus_credentials", login: "p_canopus_username", senha: "p_canopus_password", nome: "Canopus" },
  rodobens: { rpc: "set_user_rodobens_credentials", login: "p_rodobens_username", senha: "p_rodobens_password", nome: "Rodobens" },
};

const LOGIN_ATUAL: Record<Editavel, (r: RegistroConexoes | undefined) => string | null | undefined> = {
  servopa: (r) => r?.servopa_username,
  canopus: (r) => r?.canopus_username,
  rodobens: (r) => r?.rodobens_username,
};

/** Cartões do Meu perfil. O usuário conecta Servopa, Canopus e Rodobens dele mesmo. */
export const CartaoConexoes = ({ superAdmin }: { superAdmin: boolean }) => {
  const record = useRecordContext<RegistroConexoes>();
  const refresh = useRefresh();
  const notify = useNotify();
  const [editando, setEditando] = useState<Editavel | null>(null);
  const [login, setLogin] = useState("");
  const [senha, setSenha] = useState("");
  const [salvando, setSalvando] = useState(false);
  const whatsapp = useConexaoWhatsapp(record?.id);
  const [conectandoWhatsapp, setConectandoWhatsapp] = useState(false);
  const azos = useConexaoAzosPortal(record?.id);
  const [conectandoAzos, setConectandoAzos] = useState(false);
  const { google, indoAoGoogle, irAoGoogle, desconectarDoGoogle } = useAcoesGoogle(record?.id);

  const desconectarAzos = async () => {
    try {
      await desconectarAzosPortal();
      notify("Azos desconectada.");
      await azos.recarregar();
    } catch {
      notify("Não foi possível desconectar a Azos.", { type: "error" });
    }
  };

  const [testando, setTestando] = useState(false);
  const testar = async () => {
    if (record?.id == null) return;
    setTestando(true);
    try {
      await enviarTesteWhatsapp(record.id);
      notify("Mensagem de teste enviada ao WhatsApp.");
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setTestando(false);
    }
  };

  const desconectar = async () => {
    try {
      await desconectarWhatsapp();
      notify("WhatsApp desconectado.");
      await whatsapp.recarregar();
    } catch {
      notify("Não foi possível desconectar o WhatsApp.", { type: "error" });
    }
  };

  const abrir = (codigo: Editavel) => {
    setLogin(LOGIN_ATUAL[codigo](record) ?? "");
    setSenha("");
    setEditando(codigo);
  };

  const salvar = async () => {
    if (!editando || record?.id == null) return;
    const cfg = RPC[editando];
    setSalvando(true);
    const { error } = await supabase.rpc(cfg.rpc as never, {
      p_crm_user_id: Number(record.id),
      [cfg.login]: login.trim() || null,
      [cfg.senha]: senha.trim() || null,
    } as never);
    setSalvando(false);
    if (error) {
      notify("Não foi possível salvar a conexão.", { type: "error" });
      return;
    }
    notify(`Conexão ${cfg.nome} salva.`);
    setEditando(null);
    refresh();
  };

  return (
    <Card className="border-t-[3px] border-t-ouro">
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-muted-foreground">Conexões</h2>
          <p className="text-xs text-muted-foreground">Contas externas usadas pelo sistema em nome do usuário.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {conexoesDoUsuario(record, superAdmin, whatsapp.linha, azos.linha, google.linha).map((c) => {
            const editavel = c.codigo in RPC;
            return (
              <div key={c.codigo} className="flex flex-col gap-2.5 rounded-[10px] border p-4">
                <div className="flex items-center gap-2.5">
                  <IconeConexao codigo={c.codigo} sigla={c.sigla} />
                  <div className="flex min-w-0 flex-col">
                    <span className="text-sm font-semibold">{c.nome}</span>
                    <span className="text-xs text-muted-foreground">{c.descricao}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <SeloConexao estado={c.estado} />
                  {editavel ? (
                    <Button
                      type="button"
                      size="sm"
                      variant={c.estado === "conectado" ? "outline" : "default"}
                      onClick={() => abrir(c.codigo as Editavel)}
                    >
                      {c.estado === "conectado" ? "Alterar" : "Conectar"}
                    </Button>
                  ) : c.codigo === "whatsapp" ? (
                    c.estado === "conectado" ? (
                      <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => void testar()} disabled={testando}>
                          {testando ? "Enviando..." : "Enviar teste"}
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={desconectar}>
                          Desconectar
                        </Button>
                      </div>
                    ) : (
                      <Button type="button" size="sm" onClick={() => setConectandoWhatsapp(true)}>
                        Conectar
                      </Button>
                    )
                  ) : c.codigo === "azos" ? (
                    c.estado === "conectado" ? (
                      <Button type="button" size="sm" variant="outline" onClick={desconectarAzos}>
                        Desconectar
                      </Button>
                    ) : (
                      <Button type="button" size="sm" onClick={() => setConectandoAzos(true)}>
                        Conectar
                      </Button>
                    )
                  ) : c.codigo === "gmail" ? (
                    c.estado === "conectado" ? (
                      <Button type="button" size="sm" variant="outline" onClick={desconectarDoGoogle}>
                        Desconectar
                      </Button>
                    ) : (
                      <Button type="button" size="sm" onClick={() => void irAoGoogle()} disabled={indoAoGoogle}>
                        {indoAoGoogle ? "Abrindo..." : "Conectar"}
                      </Button>
                    )
                  ) : c.estado === "em_breve" ? (
                    <Button type="button" size="sm" variant="outline" disabled>
                      Conectar
                    </Button>
                  ) : null}
                </div>
                <span className="text-xs text-muted-foreground">{c.detalhe}</span>
              </div>
            );
          })}
        </div>
      </CardContent>

      <Dialog open={editando != null} onOpenChange={(o) => !o && setEditando(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conexão {editando ? RPC[editando].nome : ""}</DialogTitle>
            <DialogDescription>Login em branco remove o acesso. Senha em branco mantém a atual.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="conexao-login">Login</Label>
              <Input
                id="conexao-login"
                autoComplete="off"
                value={login}
                onChange={(e) =>
                  setLogin(editando === "servopa" ? e.target.value.replace(/\D/g, "") : e.target.value.trim())
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="conexao-senha">Senha</Label>
              <Input
                id="conexao-senha"
                type="password"
                autoComplete="new-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button type="button" onClick={salvar} disabled={salvando}>
              {salvando ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DialogoWhatsapp
        aberto={conectandoWhatsapp}
        aoFechar={() => setConectandoWhatsapp(false)}
        recarregar={whatsapp.recarregar}
      />

      <DialogoAzosPortal
        aberto={conectandoAzos}
        emailAtual={azos.linha?.email}
        aoFechar={() => setConectandoAzos(false)}
        recarregar={azos.recarregar}
      />
    </Card>
  );
};
